import type { NormalizedLandmark } from "@mediapipe/tasks-vision";

export interface Angles {
  hip: { left: number | null; right: number | null };
  elbow: { left: number | null; right: number | null };
  knee: { left: number | null; right: number | null };
}

// MediaPipe Pose landmark indices
const LM = {
  LEFT_SHOULDER: 11,
  RIGHT_SHOULDER: 12,
  LEFT_ELBOW: 13,
  RIGHT_ELBOW: 14,
  LEFT_WRIST: 15,
  RIGHT_WRIST: 16,
  LEFT_HIP: 23,
  RIGHT_HIP: 24,
  LEFT_KNEE: 25,
  RIGHT_KNEE: 26,
  LEFT_ANKLE: 27,
  RIGHT_ANKLE: 28,
} as const;

function calculateAngle(
  a: NormalizedLandmark,
  b: NormalizedLandmark,
  c: NormalizedLandmark,
): number {
  const ba = { x: a.x - b.x, y: a.y - b.y };
  const bc = { x: c.x - b.x, y: c.y - b.y };

  const dot = ba.x * bc.x + ba.y * bc.y;
  const magBA = Math.sqrt(ba.x * ba.x + ba.y * ba.y);
  const magBC = Math.sqrt(bc.x * bc.x + bc.y * bc.y);

  const cosine = Math.max(-1, Math.min(1, dot / (magBA * magBC)));
  return (Math.acos(cosine) * 180) / Math.PI;
}

export function getAllAngles(landmarks: NormalizedLandmark[]): Angles {
  const angles: Angles = {
    hip: { left: null, right: null },
    elbow: { left: null, right: null },
    knee: { left: null, right: null },
  };

  try {
    // Hip angles (shoulder-hip-knee)
    angles.hip.left = calculateAngle(
      landmarks[LM.LEFT_SHOULDER],
      landmarks[LM.LEFT_HIP],
      landmarks[LM.LEFT_KNEE],
    );
    angles.hip.right = calculateAngle(
      landmarks[LM.RIGHT_SHOULDER],
      landmarks[LM.RIGHT_HIP],
      landmarks[LM.RIGHT_KNEE],
    );

    // Elbow angles (shoulder-elbow-wrist)
    angles.elbow.left = calculateAngle(
      landmarks[LM.LEFT_SHOULDER],
      landmarks[LM.LEFT_ELBOW],
      landmarks[LM.LEFT_WRIST],
    );
    angles.elbow.right = calculateAngle(
      landmarks[LM.RIGHT_SHOULDER],
      landmarks[LM.RIGHT_ELBOW],
      landmarks[LM.RIGHT_WRIST],
    );

    // Knee angles (hip-knee-ankle)
    angles.knee.left = calculateAngle(
      landmarks[LM.LEFT_HIP],
      landmarks[LM.LEFT_KNEE],
      landmarks[LM.LEFT_ANKLE],
    );
    angles.knee.right = calculateAngle(
      landmarks[LM.RIGHT_HIP],
      landmarks[LM.RIGHT_KNEE],
      landmarks[LM.RIGHT_ANKLE],
    );
  } catch {
    // Some landmarks may not be visible
  }

  return angles;
}

// Skeleton connections for drawing
export const SKELETON_CONNECTIONS: [number, number][] = [
  [LM.LEFT_SHOULDER, LM.RIGHT_SHOULDER],
  [LM.LEFT_SHOULDER, LM.LEFT_ELBOW],
  [LM.LEFT_ELBOW, LM.LEFT_WRIST],
  [LM.RIGHT_SHOULDER, LM.RIGHT_ELBOW],
  [LM.RIGHT_ELBOW, LM.RIGHT_WRIST],
  [LM.LEFT_SHOULDER, LM.LEFT_HIP],
  [LM.RIGHT_SHOULDER, LM.RIGHT_HIP],
  [LM.LEFT_HIP, LM.RIGHT_HIP],
  [LM.LEFT_HIP, LM.LEFT_KNEE],
  [LM.LEFT_KNEE, LM.LEFT_ANKLE],
  [LM.RIGHT_HIP, LM.RIGHT_KNEE],
  [LM.RIGHT_KNEE, LM.RIGHT_ANKLE],
];

export { LM };
